import { useState } from '@hookstate/core';
import { FC, useCallback, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { CurrentGamePanelDesktop } from '../../components/CurrentGamePanel/desktop';
import { CurrentGamePanelMobile } from '../../components/CurrentGamePanel/mobile';
import { Part } from '../../components/GamePart';
import { useBoughtTickets } from '../../hooks/use-bought-tickets';
import { useCountdown } from '../../hooks/use-countdown';
import { useFormatCurrency } from '../../hooks/use-format-currency';
import { useGamePart } from '../../hooks/use-game-part';
import { useGameType } from '../../hooks/use-game-type';
import { useJackpot } from '../../hooks/use-jackpot';
import { useSoundHandler } from '../../hooks/use-sound-handler';
import { useTicketPrice } from '../../hooks/use-ticket-price';
import {
    autoBuy,
    calls,
    current,
    meta,
    next,
    screenConfig,
} from '../../stores/backend';

export const CurrentGamePanel: FC = () => {
    const { t } = useTranslation();

    const $current = useState(current);
    const $next = useState(next);
    const $calls = useState(calls);
    const $autoBuy = useState(autoBuy);
    const $meta = useState(meta);
    const {
        screenConfig: { display },
    } = useState(screenConfig);

    const { seconds } = useCountdown(new Date($meta.timer.date.value));
    const bought = useBoughtTickets($current);

    const game = bought ? $current : $next;
    const playerCards = game.game_cards.value || 0;
    const players = game.game_players.value || 0;

    const gameType = useGameType(game);
    const ticketPrice = useTicketPrice($next);
    const formatCurrency = useFormatCurrency(game, $meta);
    const formatCurrencyNext = useFormatCurrency($next, $meta);
    const { part, prize, prizeString, patternName, allGameParts } = useGamePart(
        game,
        $meta
    );

    const gameCurrency = $next.game_currency.value;
    const gamePrize =
        prizeString?.length > 1 &&
        typeof prizeString[$calls.length - 1] !== 'undefined'
            ? formatCurrency(
                  prizeString[$calls.length - 1],
                  game.game_currency.value
              )
            : prize;
    const $ticketPrice = ticketPrice
        ? formatCurrencyNext(ticketPrice, gameCurrency)
        : t('buyBtn.buy.free');

    const gameParts = useMemo(
        () => Object.keys(allGameParts).length,
        [allGameParts]
    );

    const parts = useMemo(
        () =>
            Object.values(game.game_parts.value || {}).map((part) => ({
                patterns: part.pat_code || [[]],
                matches: part.num_matches || 1,
            })),
        [game.game_parts.value]
    );

    const jackpot = useJackpot(game, $meta);

    const onInfoBox = useCallback(() => {
        $meta.modals.gameInfoFireBallEnabled.set(true);
        $meta.modals.gameInfo.set(true);
    }, [$meta.modals.gameInfo, $meta.modals.gameInfoFireBallEnabled]);

    const onBuy = () => {
        $meta.modals.buy.set(true);
    };

    const $onBuy = useSoundHandler(onBuy, 'click');

    const Component = isMobile
        ? CurrentGamePanelMobile
        : CurrentGamePanelDesktop;

    return (
        <Component
            {...jackpot}
            onInfoBox={onInfoBox}
            onBuy={$onBuy}
            seconds={seconds}
            gameTitle={game.game_desc.value}
            gameType={gameType}
            gameParts={gameParts as Part}
            currentPart={part}
            fireBall={game.fireball?.value}
            patternName={patternName}
            gamePlayers={players}
            gameCards={playerCards}
            parts={parts}
            patternType={gameType}
            gamePrize={gamePrize}
            gameLinked={game.game_linked?.value}
            gameContinued={game.game_continued?.value}
            showTotalTickets={display.showTotalTickets.value}
            autoBuy={$autoBuy.activation.value}
            ticketPrice={$ticketPrice}
            isGameLive={false} // TODO: Implement isGameLive value
        />
    );
};
